import { useEffect, useMemo } from 'react';
import { createBackgroundReadQueue } from '../utils/backgroundReadQueue';
import { getAdminPersonalUnreadCount } from '../api/adminChat';
import { getAdminGroupChatUnreadCount } from '../api/adminGroupChat';
import { getArtistPersonalUnreadCount, getArtistGroupChatUnreadCount } from '../api/artistChat';
import { getAdminEventChatUnreadCounts, getArtistEventChatUnreadCounts } from '../api/eventChat';
import { getChatMentionUnreadSummary } from '../api/chatMentions';

export function useChatUnreadRequests(scope: string) {
  const resource = useMemo(() => {
    const queue = createBackgroundReadQueue();
    const count = (data: any) => { if (!Number.isSafeInteger(data?.unreadCount) || data.unreadCount < 0) throw new Error('Invalid unread count'); };
    const rows = (data: any, key: string) => {
      if (!Array.isArray(data?.unreadCounts) || data.unreadCounts.some((row: any) => !row?.[key] || row.unread_count == null || !Number.isSafeInteger(Number(row.unread_count)) || Number(row.unread_count) < 0)) throw new Error('Invalid unread counts');
    };
    const request = <T,>(key: string, load: () => Promise<T>, validate: (value: T) => void) => queue.run(key, async () => {
      const result = await load(); validate(result); return result;
    });
    return {
      queue, mounted: false,
      personal: (artist = false) => request(`personal:${artist}`, artist ? getArtistPersonalUnreadCount : getAdminPersonalUnreadCount, count),
      group: (artist = false) => request(`group:${artist}`, artist ? getArtistGroupChatUnreadCount : getAdminGroupChatUnreadCount, (data) => rows(data, 'thread_id')),
      events: (artist = false) => request(`events:${artist}`, artist ? getArtistEventChatUnreadCounts : getAdminEventChatUnreadCounts, (data) => rows(data, 'event_id')),
      mentions: () => request('mentions', getChatMentionUnreadSummary, (data) => {
        if (typeof data?.event !== 'boolean' || typeof data?.group !== 'boolean') throw new Error('Invalid mention summary');
      }),
    };
  }, [scope]);
  useEffect(() => {
    resource.mounted = true;
    const onVisibility = () => { if (document.visibilityState === 'hidden') resource.queue.pause(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); resource.mounted = false; queueMicrotask(() => { if (!resource.mounted) resource.queue.dispose(); }); };
  }, [resource]);
  return resource;
}
